import { createColumnHelper } from '@tanstack/react-table';
import {
  Badge,
  IconButton,
  Flex,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdRefresh, MdDelete } from 'react-icons/md';
import { InviteRow } from '../index';

const columnHelper = createColumnHelper<InviteRow>();

const statusColorMap: Record<string, string> = {
  pending: 'orange',
  accepted: 'green',
  rejected: 'red',
  cancelled: 'gray',
};

const typeColorMap: Record<string, string> = {
  seat: 'blue',
  supplier: 'purple',
  client: 'teal',
};

interface UseColumnsParams {
  onResend: (id: string, type?: string) => void;
  onDelete: (id: string) => void;
}

export default function useColumns({ onResend, onDelete }: UseColumnsParams) {
  const textColor = useColorModeValue('navy.700', 'white');

  return [
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Email
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('fullName', {
      id: 'fullName',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Name
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('businessName', {
      id: 'businessName',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Business
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Type
        </Text>
      ),
      cell: (info) => {
        const val = info.getValue();
        return val ? (
          <Badge
            colorScheme={typeColorMap[val] || 'gray'}
            fontSize="xs"
            borderRadius="6px"
          >
            {val}
          </Badge>
        ) : (
          <Text color="gray.400" fontSize="sm">
            -
          </Text>
        );
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Status
        </Text>
      ),
      cell: (info) => {
        const val = info.getValue();
        return (
          <Badge
            colorScheme={statusColorMap[val || ''] || 'gray'}
            fontSize="xs"
            borderRadius="6px"
          >
            {val || '-'}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('role', {
      id: 'role',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Role
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Created
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue()
            ? new Date(info.getValue() as Date).toLocaleDateString()
            : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('updatedAt', {
      id: 'updatedAt',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Expiry
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue()
            ? new Date(info.getValue() as Date).toLocaleDateString()
            : '-'}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Actions
        </Text>
      ),
      cell: (info) => {
        const row = info.row.original;
        return (
          <Flex gap="4px">
            {row.status === 'pending' && (
              <IconButton
                aria-label="Resend invite"
                icon={<MdRefresh />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => onResend(row.id!, row.type)}
              />
            )}
            <IconButton
              aria-label="Delete invite"
              icon={<MdDelete />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => onDelete(row.id!)}
            />
          </Flex>
        );
      },
    }),
  ];
}
